import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Calendar,
  TrendingUp,
  TrendingDown,
  Weight,
  RotateCcw,
  Brain,
  Loader2,
  Trophy,
  Heart,
  Flame,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { PersonalBestIcon } from './PersonalBestIcon';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import { useUnitFormatters } from '@/hooks/useUnits';
import { kgToDisplayWeight } from '@/lib/units';
import { isGpsCardioWorkoutType, GPS_CARDIO_EXERCISE_NAMES } from '@/lib/gpsCardioWorkout';
import { fetchAllExerciseRowsForUser, parseCardioNotes } from '@/lib/fetchExerciseHistoryRows';
import { formatPaceSecPerKm } from '@/lib/formatPace';
import { RunHistoryDetailModal } from '@/components/RunHistoryDetailModal';
import type { Workout } from '@/hooks/useWorkouts';
import { PENDING_CHAT_AUTO_SEND_KEY } from '@/lib/runningCoachPlans';

interface SetDetails {
  reps: number;
  weight_kg: number | null;
  distance_km?: number | null;
  duration_seconds?: number | null;
  drop_set?: boolean;
}

interface ExerciseHistory {
  workout_id: string;
  workout_date: string;
  workout_titles: string[];
  allSets: SetDetails[];
  workout_duration: number | null;
  workout_type?: string;
  isWeightPB?: boolean;
  isRepsPB?: boolean;
  isDistancePB?: boolean;
  isPacePB?: boolean;
  maxWeight: number;
  maxReps: number;
  totalDistance?: number;
  /** minutes per km */
  avgPaceMinPerKm?: number;
  avgHeartRate?: number | null;
  calories?: number | null;
}

interface ExerciseHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  exerciseName: string;
}

const isCardioExerciseName = (name: string) =>
  GPS_CARDIO_EXERCISE_NAMES.some((n) => name.toLowerCase() === n.toLowerCase()) ||
  // Use word boundaries — bare "row" falsely matched "cable row" gym lifts.
  /\b(run|running|walk|walking|hik(?:e|ing)|bike|biking|cycl(?:e|ing)|cardio|rowing(?:\s+machine)?|rower)\b/i.test(
    name,
  );

export const ExerciseHistoryModal = ({ isOpen, onClose, exerciseName }: ExerciseHistoryModalProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { units, formatWeight, weightLabel } = useUnitFormatters();
  const [history, setHistory] = useState<ExerciseHistory[]>([]);
  const [loading, setLoading] = useState(false);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [isCardio, setIsCardio] = useState(false);
  const [detailWorkout, setDetailWorkout] = useState<Workout | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [personalBest, setPersonalBest] = useState<{
    maxWeight: { value: number; date: string } | null;
    maxReps: { value: number; date: string } | null;
    maxDistance: { value: number; date: string } | null;
    bestPace: { value: number; date: string } | null;
  }>({
    maxWeight: null,
    maxReps: null,
    maxDistance: null,
    bestPace: null,
  });

  useEffect(() => {
    if (isOpen && exerciseName && user) {
      loadExerciseHistory();
    }
  }, [isOpen, exerciseName, user]);

  const loadExerciseHistory = async () => {
    if (!user || !exerciseName) return;

    setLoading(true);
    try {
      const data = await fetchAllExerciseRowsForUser(user.id, { exerciseName });

      const cardio =
        isCardioExerciseName(exerciseName) ||
        !!(data && data.length > 0 && isGpsCardioWorkoutType((data[0] as any)?.workouts?.workout_type));
      setIsCardio(cardio);

      const workoutMap = new Map<
        string,
        {
          workout_id: string;
          workout_date: string;
          workout_titles: string[];
          workout_duration: number | null;
          workout_type: string;
          allSets: SetDetails[];
          maxReps: number;
          maxWeight: number;
          totalDistance: number;
          totalDuration: number;
          avgHeartRate: number | null;
          calories: number | null;
        }
      >();

      (data || []).forEach((item: any) => {
        const workoutRaw = item?.workouts;
        const workout = Array.isArray(workoutRaw) ? workoutRaw[0] : workoutRaw;
        const workoutId: string | undefined = workout?.id;
        const workoutKey = workoutId || String(workout?.completed_at || '');
        if (!workoutKey) return;

        const notesMeta = parseCardioNotes(item.notes);
        const currentReps = item.reps || 0;
        const currentWeight = item.weight_kg || 0;
        const currentDistance =
          Number(item.distance_km) || Number(notesMeta.distance) || 0;
        const currentDuration =
          Number(item.duration_seconds) || Number(notesMeta.durationSeconds) || 0;
        const avgHr = notesMeta.avgBpm != null ? Number(notesMeta.avgBpm) : null;
        const calories =
          workout?.calories_burned != null ? Number(workout.calories_burned) : null;

        const setsFromRecord: SetDetails[] =
          !cardio && Array.isArray(item.set_details) && item.set_details.length > 0
            ? item.set_details.map((s: any) => ({
                reps: s?.reps || 0,
                weight_kg: (s?.weight_kg ?? s?.weight ?? null) || null,
                drop_set: !!(s?.drop_set ?? s?.isDropSet),
              }))
            : [
                {
                  reps: currentReps,
                  weight_kg: currentWeight > 0 ? currentWeight : null,
                  distance_km: currentDistance > 0 ? currentDistance : null,
                  duration_seconds: currentDuration > 0 ? currentDuration : null,
                },
              ];

        const recordMaxReps = Math.max(...setsFromRecord.map((s) => s.reps || 0), 0);
        const recordMaxWeight = Math.max(...setsFromRecord.map((s) => s.weight_kg || 0), 0);
        const existing = workoutMap.get(workoutKey);

        if (existing) {
          existing.allSets.push(...setsFromRecord);
          existing.maxReps = Math.max(existing.maxReps, recordMaxReps);
          existing.maxWeight = Math.max(existing.maxWeight, recordMaxWeight);
          existing.totalDistance += currentDistance;
          existing.totalDuration += currentDuration;
          if (avgHr && !existing.avgHeartRate) existing.avgHeartRate = avgHr;
          if (calories != null) existing.calories = calories;
          if (!existing.workout_titles.includes(workout.title)) {
            existing.workout_titles.push(workout.title);
          }
        } else {
          workoutMap.set(workoutKey, {
            workout_id: workoutId || workoutKey,
            workout_date: workout.completed_at,
            workout_titles: [workout.title],
            workout_duration: workout.duration_minutes,
            workout_type: workout.workout_type || 'strength',
            allSets: [...setsFromRecord],
            maxReps: recordMaxReps,
            maxWeight: recordMaxWeight,
            totalDistance: currentDistance,
            totalDuration: currentDuration,
            avgHeartRate: avgHr,
            calories,
          });
        }
      });

      const formattedHistory: ExerciseHistory[] = Array.from(workoutMap.values())
        .sort((a, b) => new Date(b.workout_date).getTime() - new Date(a.workout_date).getTime())
        .map((workout) => ({
          workout_id: workout.workout_id,
          workout_date: workout.workout_date,
          workout_titles: workout.workout_titles,
          allSets: workout.allSets,
          workout_duration: workout.workout_duration,
          workout_type: workout.workout_type,
          maxWeight: workout.maxWeight,
          maxReps: workout.maxReps,
          totalDistance: workout.totalDistance,
          avgPaceMinPerKm:
            workout.totalDistance > 0 && workout.totalDuration > 0
              ? workout.totalDuration / 60 / workout.totalDistance
              : 0,
          avgHeartRate: workout.avgHeartRate,
          calories: workout.calories,
        }));

      let maxWeight = { value: 0, date: '' };
      let maxReps = { value: 0, date: '' };
      let maxDistance = { value: 0, date: '' };
      let bestPace = { value: Infinity, date: '' };

      formattedHistory.forEach((record) => {
        if (cardio) {
          if (record.totalDistance && record.totalDistance > maxDistance.value) {
            maxDistance = { value: record.totalDistance, date: record.workout_date };
          }
          if (
            record.avgPaceMinPerKm &&
            record.avgPaceMinPerKm > 0 &&
            record.avgPaceMinPerKm < bestPace.value
          ) {
            bestPace = { value: record.avgPaceMinPerKm, date: record.workout_date };
          }
        } else {
          if (record.maxWeight && record.maxWeight > maxWeight.value) {
            maxWeight = { value: record.maxWeight, date: record.workout_date };
          }
          if (record.maxReps && record.maxReps > maxReps.value) {
            maxReps = { value: record.maxReps, date: record.workout_date };
          }
        }
      });

      const historyWithPBs = formattedHistory.map((record) => ({
        ...record,
        isWeightPB: !cardio && record.maxWeight === maxWeight.value && maxWeight.value > 0,
        isRepsPB: !cardio && record.maxReps === maxReps.value && maxReps.value > 0,
        isDistancePB: cardio && record.totalDistance === maxDistance.value && maxDistance.value > 0,
        isPacePB: cardio && record.avgPaceMinPerKm === bestPace.value && bestPace.value !== Infinity,
      }));

      setHistory(historyWithPBs);
      setPersonalBest({
        maxWeight: !cardio && maxWeight.value > 0 ? maxWeight : null,
        maxReps: !cardio && maxReps.value > 0 ? maxReps : null,
        maxDistance: cardio && maxDistance.value > 0 ? maxDistance : null,
        bestPace: cardio && bestPace.value !== Infinity ? bestPace : null,
      });
    } catch (error) {
      console.error('Error loading exercise history:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPerformanceTrend = (index: number) => {
    if (index === history.length - 1) return null;
    const current = history[index];
    const previous = history[index + 1];
    const cardioSession =
      isCardio || isGpsCardioWorkoutType(current.workout_type);

    if (cardioSession) {
      const currentDistance = current.totalDistance || 0;
      const previousDistance = previous.totalDistance || 0;
      if (currentDistance > previousDistance) return 'improved';
      if (currentDistance < previousDistance) return 'declined';
      const currentPace = current.avgPaceMinPerKm || Infinity;
      const previousPace = previous.avgPaceMinPerKm || Infinity;
      if (currentPace < previousPace) return 'improved';
      if (currentPace > previousPace) return 'declined';
      return 'same';
    }

    const currentVolume = current.allSets.reduce(
      (sum, set) => sum + (set.weight_kg || 0) * set.reps,
      0,
    );
    const previousVolume = previous.allSets.reduce(
      (sum, set) => sum + (set.weight_kg || 0) * set.reps,
      0,
    );
    if (currentVolume > previousVolume) return 'improved';
    if (currentVolume < previousVolume) return 'declined';
    return 'same';
  };

  const openWorkoutDetail = async (workoutId: string) => {
    if (!user || !workoutId) return;
    try {
      const { data, error } = await supabase
        .from('workouts')
        .select('*, exercises (*)')
        .eq('id', workoutId)
        .eq('user_id', user.id)
        .maybeSingle();
      if (error) throw error;
      if (!data) {
        toast.error(t('components.exerciseHistory.analyzeFailed'));
        return;
      }
      const workout: Workout = {
        ...data,
        exercises: (data.exercises || []).map((exercise: any) => ({
          ...exercise,
          category: (exercise.category as 'Strength' | 'Cardio') || 'Strength',
          set_details: exercise.set_details,
        })),
      };
      if (isGpsCardioWorkoutType(workout.workout_type) || isCardio) {
        setDetailWorkout(workout);
        setDetailOpen(true);
      }
    } catch (e) {
      console.error('Failed to open workout detail', e);
      toast.error(t('components.exerciseHistory.analyzeFailed'));
    }
  };

  const handleAskArnold = async () => {
    if (!user || !exerciseName || history.length === 0) return;

    setAnalysisLoading(true);
    try {
      const lines = history.slice(0, 15).map((h) => {
        if (isCardio || isGpsCardioWorkoutType(h.workout_type)) {
          const paceSec = (h.avgPaceMinPerKm || 0) * 60;
          return `- ${new Date(h.workout_date).toLocaleDateString()}: ${h.totalDistance?.toFixed(2) ?? '?'} km, pace ${formatPaceSecPerKm(paceSec)}/km${h.avgHeartRate ? `, avg HR ${Math.round(h.avgHeartRate)}` : ''}${h.calories ? `, ${Math.round(h.calories)} kcal` : ''}`;
        }
        return `- ${new Date(h.workout_date).toLocaleDateString()}: ${h.maxWeight}kg × ${h.maxReps} reps (${h.allSets.length} sets)`;
      });

      const msg = `Analyze my "${exerciseName}" progress across these sessions and give concrete coaching advice:\n\n${lines.join('\n')}\n\nSummarize trends, personal bests, and what I should focus on next.`;

      try {
        sessionStorage.setItem(PENDING_CHAT_AUTO_SEND_KEY, msg);
      } catch { /* ignore */ }

      onClose();
      navigate('/chat', { state: { message: msg } });
    } catch (error: any) {
      console.error('Error preparing Arnold analysis:', error);
      toast.error(t('components.exerciseHistory.analyzeFailed'));
    } finally {
      setAnalysisLoading(false);
    }
  };

  const showCardioPb =
    personalBest.maxDistance || personalBest.bestPace;
  const showStrengthPb = personalBest.maxWeight || personalBest.maxReps;

  return (
    <>
      <RunHistoryDetailModal
        workout={detailWorkout}
        open={detailOpen}
        onClose={() => {
          setDetailOpen(false);
          setDetailWorkout(null);
        }}
      />

      {/* Hide history while run detail is open — nested Dialog + MapKit was freezing / crashing WebView */}
      <Dialog open={isOpen && !detailOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
        <DialogContent className="max-w-[95vw] sm:max-w-4xl h-[90vh] sm:h-[85vh] flex flex-col p-4 sm:p-6 [&>button]:hidden">
          <DialogHeader className="pb-3">
            <DialogTitle className="text-base sm:text-xl font-bold flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                <span className="text-sm sm:text-base truncate">{t('components.exerciseHistory.title')}</span>
              </div>
              <Button
                onClick={handleAskArnold}
                disabled={analysisLoading || history.length === 0}
                className="bg-gradient-primary hover:bg-gradient-primary/90 text-black text-xs sm:text-sm w-full sm:w-auto"
                size="sm"
              >
                {analysisLoading ? (
                  <>
                    <Loader2 className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2 animate-spin" />
                    <span className="text-xs sm:text-sm">{t('components.exerciseHistory.analyzing')}</span>
                  </>
                ) : (
                  <>
                    <Brain className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                    <span className="text-xs sm:text-sm">{t('components.exerciseHistory.analyze')}</span>
                  </>
                )}
              </Button>
            </DialogTitle>
            <p className="text-xs sm:text-sm text-muted-foreground">{exerciseName}</p>
          </DialogHeader>

          <div className="flex-1 min-h-0 flex flex-col gap-3 sm:gap-4">
            {(showCardioPb || showStrengthPb) && (
              <Card className="bg-gradient-card border-primary/20">
                <CardContent className="p-3 sm:p-4">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3">
                    <Trophy className="h-4 w-4 sm:h-5 sm:w-5 text-accent" />
                    <span className="font-semibold text-foreground text-base sm:text-lg">
                      {t('components.exerciseHistory.personalBests')}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 sm:gap-4">
                    {personalBest.maxDistance && (
                      <div className="text-center p-2 sm:p-3 bg-background/20 rounded-lg border border-border/30">
                        <TrendingUp className="h-4 w-4 sm:h-6 sm:w-6 mx-auto mb-1 sm:mb-2 text-secondary" />
                        <p className="text-lg sm:text-2xl font-bold text-foreground">
                          {personalBest.maxDistance.value.toFixed(2)} km
                        </p>
                        <p className="text-xs sm:text-sm text-muted-foreground">
                          {t('components.exerciseHistory.longestDistance')}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(personalBest.maxDistance.date).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                    {personalBest.bestPace && (
                      <div className="text-center p-2 sm:p-3 bg-background/20 rounded-lg border border-border/30">
                        <TrendingDown className="h-4 w-4 sm:h-6 sm:w-6 mx-auto mb-1 sm:mb-2 text-secondary" />
                        <p className="text-lg sm:text-2xl font-bold text-foreground">
                          {formatPaceSecPerKm(personalBest.bestPace.value * 60)}
                        </p>
                        <p className="text-xs sm:text-sm text-muted-foreground">
                          {t('components.exerciseHistory.bestPaceMinKm')}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(personalBest.bestPace.date).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                    {personalBest.maxWeight && (
                      <div className="text-center p-2 sm:p-3 bg-background/20 rounded-lg border border-border/30">
                        <Weight className="h-4 w-4 sm:h-6 sm:w-6 mx-auto mb-1 sm:mb-2 text-secondary" />
                        <p className="text-lg sm:text-2xl font-bold text-foreground">
                          {formatWeight(personalBest.maxWeight.value)}
                        </p>
                        <p className="text-xs sm:text-sm text-muted-foreground">
                          {t('components.exerciseHistory.maxWeight')}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(personalBest.maxWeight.date).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                    {personalBest.maxReps && (
                      <div className="text-center p-2 sm:p-3 bg-background/20 rounded-lg border border-border/30">
                        <RotateCcw className="h-4 w-4 sm:h-6 sm:w-6 mx-auto mb-1 sm:mb-2 text-secondary" />
                        <p className="text-lg sm:text-2xl font-bold text-foreground">
                          {personalBest.maxReps.value}
                        </p>
                        <p className="text-xs sm:text-sm text-muted-foreground">
                          {t('components.exerciseHistory.maxReps')}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(personalBest.maxReps.date).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            <ScrollArea className="flex-1 min-h-0 pr-2 sm:pr-4">
              {loading ? (
                <div className="text-center py-8 text-sm sm:text-base text-muted-foreground">
                  {t('components.exerciseHistory.loadingHistory')}
                </div>
              ) : history.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Calendar className="h-10 w-10 sm:h-12 sm:w-12 mx-auto mb-4 opacity-50" />
                  <p className="text-sm sm:text-base">{t('components.exerciseHistory.noWorkoutsFound')}</p>
                  <p className="text-xs sm:text-sm">{t('components.exerciseHistory.firstSession')}</p>
                </div>
              ) : (
                <div className="space-y-3 sm:space-y-4 pb-20 sm:pb-24">
                  {history.map((record, index) => {
                    const trend = getPerformanceTrend(index);
                    const cardioRow =
                      isCardio || isGpsCardioWorkoutType(record.workout_type);
                    return (
                      <Card
                        key={`${record.workout_id}-${index}`}
                        className={`bg-background/50 border-border/50 ${cardioRow ? 'cursor-pointer hover:border-primary/40 transition-colors' : ''}`}
                        onClick={() => {
                          if (cardioRow) void openWorkoutDetail(record.workout_id);
                        }}
                      >
                        <CardContent className="p-3 sm:p-4">
                          <div className="flex justify-between items-start mb-2 sm:mb-3">
                            <div className="flex-1 min-w-0 pr-2">
                              <h4 className="font-semibold text-sm sm:text-base text-foreground flex items-center gap-1 sm:gap-2 flex-wrap">
                                <span className="truncate">
                                  {record.workout_titles.length > 1
                                    ? t('components.exerciseHistory.workoutsCount', {
                                        count: record.workout_titles.length,
                                      })
                                    : record.workout_titles[0]}
                                </span>
                                {record.isDistancePB && (
                                  <PersonalBestIcon type="distance" className="animate-pulse flex-shrink-0" />
                                )}
                                {record.isPacePB && (
                                  <PersonalBestIcon type="pace" className="animate-pulse flex-shrink-0" />
                                )}
                                {record.isWeightPB && (
                                  <PersonalBestIcon type="weight" className="animate-pulse flex-shrink-0" />
                                )}
                                {record.isRepsPB && (
                                  <PersonalBestIcon type="reps" className="animate-pulse flex-shrink-0" />
                                )}
                              </h4>
                              <p className="text-xs sm:text-sm text-muted-foreground">
                                {formatDistanceToNow(new Date(record.workout_date), { addSuffix: true })}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {new Date(record.workout_date).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                })}
                              </p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              {record.workout_duration && (
                                <Badge variant="outline" className="text-xs">
                                  {t('components.exerciseHistory.minutesShort', {
                                    count: record.workout_duration,
                                  })}
                                </Badge>
                              )}
                            </div>
                          </div>

                          {cardioRow ? (
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-border/30">
                              <div className="text-center">
                                <p className="text-xs text-muted-foreground">
                                  {t('components.exerciseHistory.totalDistance')}
                                </p>
                                <p className="font-semibold text-sm sm:text-base text-foreground">
                                  {t('components.exerciseHistory.kmShort', {
                                    value: (record.totalDistance || 0).toFixed(2),
                                  })}
                                </p>
                              </div>
                              <div className="text-center">
                                <p className="text-xs text-muted-foreground">
                                  {t('components.exerciseHistory.avgPace')}
                                </p>
                                <p className="font-semibold text-sm sm:text-base text-foreground">
                                  {record.avgPaceMinPerKm
                                    ? `${formatPaceSecPerKm(record.avgPaceMinPerKm * 60)} /km`
                                    : '--'}
                                </p>
                              </div>
                              <div className="text-center">
                                <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                                  <Heart className="h-3 w-3" /> Avg HR
                                </p>
                                <p className="font-semibold text-sm sm:text-base text-foreground">
                                  {record.avgHeartRate ? `${Math.round(record.avgHeartRate)}` : '—'}
                                </p>
                              </div>
                              <div className="text-center">
                                <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                                  <Flame className="h-3 w-3" /> kcal
                                </p>
                                <p className="font-semibold text-sm sm:text-base text-foreground">
                                  {record.calories != null && record.calories > 0
                                    ? Math.round(record.calories)
                                    : '—'}
                                </p>
                              </div>
                            </div>
                          ) : (
                            <div className="space-y-2 mt-2 sm:mt-3">
                              <p className="text-xs font-medium text-muted-foreground mb-1">
                                {t('components.exerciseHistory.allSets')}
                              </p>
                              <div className="grid grid-cols-1 gap-1.5 sm:gap-2">
                                {record.allSets.map((set, setIndex) => (
                                  <div
                                    key={setIndex}
                                    className="flex items-center justify-between bg-muted/30 rounded p-1.5 sm:p-2"
                                  >
                                    <span className="text-xs sm:text-sm font-medium text-foreground flex items-center gap-1">
                                      {t('components.exerciseHistory.set', { number: setIndex + 1 })}
                                      {set.drop_set ? (
                                        <Badge
                                          variant="secondary"
                                          className="text-[10px] px-1 py-0 h-4 bg-primary/15 text-primary border-primary/30"
                                        >
                                          DS
                                        </Badge>
                                      ) : null}
                                    </span>
                                    <div className="flex items-center gap-2 sm:gap-4">
                                      <span className="text-xs sm:text-sm text-foreground">
                                        {t('components.exerciseHistory.repsValue', { count: set.reps })}
                                      </span>
                                      <span className="text-xs sm:text-sm text-foreground">
                                        {`@ ${formatWeight(set.weight_kg || 0)}`}
                                      </span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                              <div className="grid grid-cols-3 gap-2 mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-border/30">
                                <div className="text-center">
                                  <p className="text-xs text-muted-foreground">
                                    {t('components.exerciseHistory.totalSets')}
                                  </p>
                                  <p className="font-semibold text-sm sm:text-base text-foreground">
                                    {record.allSets.length}
                                  </p>
                                </div>
                                <div className="text-center">
                                  <p className="text-xs text-muted-foreground">
                                    {t('components.exerciseHistory.maxWeight')}
                                  </p>
                                  <p className="font-semibold text-sm sm:text-base text-foreground">
                                    {formatWeight(record.maxWeight)}
                                  </p>
                                </div>
                                <div className="text-center">
                                  <p className="text-xs text-muted-foreground">
                                    {t('components.exerciseHistory.totalVolume')}
                                  </p>
                                  <p className="font-semibold text-sm sm:text-base text-foreground">
                                    {`${Math.round(
                                      kgToDisplayWeight(
                                        record.allSets.reduce(
                                          (sum, set) => sum + (set.weight_kg || 0) * set.reps,
                                          0,
                                        ),
                                        units,
                                      ),
                                    )} ${weightLabel}`}
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}

                          {trend && (
                            <div className="mt-3 pt-3 border-t border-border/30">
                              <div className="flex items-center gap-2">
                                {trend === 'improved' && <TrendingUp className="h-4 w-4 text-green-600" />}
                                {trend === 'declined' && <TrendingDown className="h-4 w-4 text-red-600" />}
                                <Badge
                                  variant={trend === 'improved' ? 'default' : 'secondary'}
                                  className={`text-xs ${
                                    trend === 'improved'
                                      ? 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-800'
                                      : trend === 'declined'
                                        ? 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800'
                                        : 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-900/20 dark:text-gray-300 dark:border-gray-800'
                                  }`}
                                >
                                  {trend === 'improved' && t('components.exerciseHistory.improvedPerformance')}
                                  {trend === 'declined' && t('components.exerciseHistory.declinedPerformance')}
                                  {trend === 'same' && t('components.exerciseHistory.samePerformance')}
                                </Badge>
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </ScrollArea>

            <Separator />

            <div className="flex justify-end">
              <Button onClick={onClose} variant="outline">
                {t('common.close')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
